import { useState } from 'react'
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { IconCheck, IconChevronDown } from '@tabler/icons-react'

import { cn } from 'utils/classMerge'

import Text from '../text'
import { Badge, BadgeProps } from '../badge'

export interface SelectOption {
  id: number
  name: string
  value: string
  icon?: React.ElementType
  badge?: BadgeProps
}

interface SelectProps {
  /** Label displayed above the select */
  label?: string
  /** Options to display in the select
   * @example
   * ```tsx
   * [
   *  { id: "1", name: "Option 1", value: "Option 1" },
   *  { id: "2", name: "Option 2", value: "Option 2" },
   *  { id: "3", name: "Option 3", value: "Option 3" },
   * ]
   * ```
   */
  options: SelectOption[]
  /** Placeholder text displayed when no option is selected */
  placeholder?: string
  /** Class name for the ListboxButton */
  buttonClassName?: string
  /** Value of the selected option */
  value?: string
  /** Callback function to handle the change of the selected option */
  onChange?: (value: SelectOption) => void
  /** Class name for the container */
  className?: string
  outsideValue?: SelectOption
}

const Select = ({
  label,
  options,
  placeholder = 'Select',
  className,
  buttonClassName,
  onChange,
  value,
  outsideValue,
  ...props
}: SelectProps) => {
  const [selected, setSelected] = useState<SelectOption | undefined>(
    value ? options.find((option) => option.value === value) : undefined,
  )

  const handleChange = (selectedOption: SelectOption) => {
    onChange?.(selectedOption)
    if (outsideValue?.value) return
    setSelected(selectedOption)
  }

  return (
    <Listbox value={outsideValue || selected} onChange={handleChange}>
      {({ open }) => {
        return (
          <div
            data-testid="select-container"
            className={cn('tw-relative tw-min-w-[220px]', className)}
            {...props}
          >
            {label && (
              <Label className="tw-mb-2 tw-text-left tw-block tw-text-sm tw-font-medium tw-text-zinc-950">
                {label}
              </Label>
            )}

            <ListboxButton
              className={cn(
                'tw-border-none tw-cursor-pointer tw-w-full tw-rounded-md tw-bg-white tw-pt-1.5 tw-pb-1.5 tw-pl-3 tw-pr-3 tw-text-left tw-text-zinc-950 tw-outline tw-outline-1 -tw-outline-offset-1 tw-outline-zinc-300 focus:tw-outline focus:tw-outline-2 focus:-tw-outline-offset-2 focus:tw-outline-orange-500 tw-text-sm sm:tw-text-sm/6 tw-shadow-sm tw-flex tw-items-center tw-justify-between',
                buttonClassName,
                {
                  'tw-outline-orange-500 tw-outline-2 tw--outline-offset-2':
                    open,
                },
              )}
            >
              <Text
                as="span"
                className="tw-col-start-1 tw-row-start-1 tw-truncate tw-pr-6"
              >
                {outsideValue?.name || selected?.name || placeholder}
              </Text>
              <IconChevronDown
                aria-hidden="true"
                className={cn(
                  'tw-col-start-1 tw-row-start-1 tw-size-5 tw-self-center tw-justify-self-end tw-text-zinc-400 sm:tw-size-4 tw-transition-transform tw-duration-200',
                  {
                    'tw--rotate-180': open,
                  },
                )}
              />
            </ListboxButton>

            <ListboxOptions
              transition
              className="tw-custom-scrollbar tw-text-left tw-absolute tw-z-10 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-md tw-bg-white tw-py-0 tw-text-sm tw-shadow-lg tw-ring-1 tw-ring-black/5 focus:tw-outline-none data-[closed]:data-[leave]:tw-opacity-0 data-[leave]:tw-transition data-[leave]:tw-duration-100 data-[leave]:tw-ease-in sm:tw-text-base"
            >
              {options.map((option) => (
                <ListboxOption
                  key={option.id}
                  value={option}
                  className="tw-group tw-cursor-pointer tw-relative tw-select-none tw-py-2 tw-pl-3 tw-pr-9 tw-text-zinc-950 data-[focus]:tw-bg-orange-50 data-[focus]:tw-outline-none data-[selected]:tw-bg-orange-500 tw-flex tw-items-center tw-gap-2"
                >
                  {option.icon && (
                    <option.icon
                      data-testid="select-option-icon"
                      className="tw-size-5 tw-text-zinc-500 tw-flex-shrink-0 group-data-[selected]:tw-text-white"
                    />
                  )}
                  <Text
                    as="span"
                    className="tw-block tw-truncate tw-font-normal group-data-[selected]:tw-font-medium group-data-[selected]:tw-text-white"
                  >
                    {option.name}
                  </Text>
                  {option?.badge && <Badge {...option.badge} />}
                  <span className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-4 tw-text-white group-[&:not([data-selected])]:tw-hidden">
                    <IconCheck
                      aria-hidden="true"
                      className="tw-size-5"
                      data-testid="select-check-icon"
                    />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        )
      }}
    </Listbox>
  )
}

export default Select
